import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Warehouse, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

export default function MobileLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showZzz, setShowZzz] = useState(false);
  const zzzTimerRef = useRef(null);

  React.useEffect(() => {
    if (user) nav("/mobile/inventory", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setShowZzz(false);
    setLoading(true);
    zzzTimerRef.current = setTimeout(() => setShowZzz(true), 3000);
    try {
      await login(email.trim(), password);
      clearTimeout(zzzTimerRef.current);
      setShowZzz(false);
      toast.success("Welcome back!");
      nav("/mobile/inventory", { replace: true });
    } catch (e) {
      clearTimeout(zzzTimerRef.current);
      if (e.code === "ERR_NETWORK" || e.code === "ECONNABORTED") {
        setShowZzz(true);
      } else {
        setShowZzz(false);
        setErr(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <style>{`
      @keyframes zzz-float {
        0%   { transform: translateY(0);     opacity: 1; }
        100% { transform: translateY(-12px); opacity: 0; }
      }
      .zzz-z { display: inline-block; animation: zzz-float 1.5s ease-in infinite; font-size: 13px; color: hsl(var(--muted-foreground)); }
      .zzz-z:nth-child(1) { animation-delay: 0s;   }
      .zzz-z:nth-child(2) { animation-delay: 0.5s; }
      .zzz-z:nth-child(3) { animation-delay: 1.0s; }
    `}</style>
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6 py-12">
      {/* Logo */}
      <div className="flex flex-col items-center mb-10">
        <div className="w-16 h-16 bg-primary flex items-center justify-center rounded-2xl mb-4 shadow-brutal">
          <Warehouse className="w-8 h-8 text-primary-foreground" strokeWidth={2.5} />
        </div>
        <div className="text-xl font-semibold tracking-tight">Flowpi</div>
        <div className="text-xs tracking-widest uppercase text-zinc-400 mt-0.5">
          Workflow Control
        </div>
      </div>

      <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4">
        {/* Email */}
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="email@example.com"
              className="w-full border border-border bg-card pl-10 pr-4 py-3.5 text-sm text-foreground rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              style={{ minHeight: 52 }}
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full border border-border bg-card pl-10 pr-11 py-3.5 text-sm text-foreground rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              style={{ minHeight: 52 }}
            />
            <Button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-400"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-action-category="utility"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {err && (
          <div className="text-xs px-4 py-3 bg-semantic-danger border border-destructive/30 text-semantic-danger rounded-xl">
            {err}
          </div>
        )}

        <Button
          type="submit"
          loading={loading || showZzz}
          loadingLabel="Signing in..."
          className="h-[52px] w-full disabled:shadow-none"
          data-action-category="primary-submit"
        >
          Sign In
        </Button>

        {showZzz && (
          <div className="flex items-center justify-center gap-2 pt-1">
            <span style={{ display: "inline-flex", gap: 2, alignItems: "flex-end" }}>
              <span className="zzz-z">Z</span>
              <span className="zzz-z">Z</span>
              <span className="zzz-z">Z</span>
            </span>
            <p className="m-0 text-[13px] text-muted-foreground">
              Membangunkan server. Tungguin aja jangan di refresh ya...
            </p>
          </div>
        )}
      </form>
    </div>
    </>
  );
}
